import React from 'react';

import { useChooseFiles } from '@gogame/hooks-ts';

import ConceptDetails from 'scenes/Gallery/GalleryPage/Concepts/ConceptDetails';
import EditConceptForm from 'scenes/Gallery/GalleryPage/Concepts/EditConceptForm';
import ShareConceptForm from 'scenes/Gallery/GalleryPage/Concepts/ShareConceptForm';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/constants';
import {
  useActivate,
  useUnarchive,
  useArchive,
  useMoveToPreview,
  useRemove,
  useInactive,
  useDownloadOriginal,
} from 'scenes/Gallery/GalleryPage/Concepts/data';
import {
  useCopyFileLink,
  useExportConcepts,
} from 'scenes/Gallery/GalleryPage/Concepts/data';
import CreateCreativesForm from 'scenes/Gallery/GalleryPage/Creatives/CreateCreativesForm';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/constants';
import {
  CONCEPT_ID,
  EXCEL,
  TAB_COMMENTS,
  ZIP,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';

import Actions from './Actions';

const ConceptActions = ({
  actions,
  data,
  limit,
  onRemove,
  stateSchedules,
  onOpenChildComponent,
  color,
  withAddCreatives = true,
  withAddComment = true,
}) => {
  const { [CONCEPT.ID]: id, [CONCEPT.NAME]: name, [CONCEPT.SIZE]: size } = data;

  const { isAllowUploadPlayable } = useGalleryPermissions();

  const [archiveConcept] = useArchive();
  const [unarchiveConcept] = useUnarchive();
  const [moveToPreviewConcept] = useMoveToPreview();
  const copyFileLink = useCopyFileLink();
  const [activateConcept] = useActivate();
  const [inactivateConcept] = useInactive();
  const [removeConcept] = useRemove();
  const [exportExcelConcepts] = useExportConcepts({ type: EXCEL });
  const [exportZipConcepts] = useExportConcepts({ type: ZIP });
  const downloadOriginal = useDownloadOriginal();

  const chooseFiles = useChooseFiles({
    accept: `image/jpeg,image/jpg,image/png,image/gif,video/mp4,video/quicktime${
      isAllowUploadPlayable
        ? ',application/zip,application/x-zip-compressed,multipart/x-zip'
        : ''
    }`,
  });

  const handleAddCreatives = async () => {
    const files = await chooseFiles();
    onOpenChildComponent(CreateCreativesForm, {
      initialValues: {
        [CONCEPT_ID]: id,
        [CREATIVE.CREATIVES]: files.map((file) => ({
          [CREATIVE.FILE]: file,
          [CREATIVE.DESCRIPTION]: `Original File Name: ${file?.name}`,
        })),
      },
    });
  };

  const handleEdit = () => onOpenChildComponent(EditConceptForm, { id });

  const handleRemove = async () => {
    await removeConcept({ id, name });
    onRemove?.(data);
  };

  const handleArchive = () => archiveConcept({ id, name, stateSchedules });

  const handleUnarchive = () => unarchiveConcept({ id, name, stateSchedules });

  const handleMoveToPreview = () =>
    moveToPreviewConcept({ id, name, stateSchedules });

  const handleActivate = () => activateConcept({ id, name, stateSchedules });

  const handleInactivate = () =>
    inactivateConcept({ id, name, stateSchedules });

  const handleCopyLink = () => copyFileLink({ id });

  const handleExport = () =>
    exportZipConcepts(
      {
        ids: [id],
      },
      { size }
    );

  const handleExportOriginal = () => downloadOriginal({ id });

  const handleExportToExcel = () => exportExcelConcepts({ ids: [id] });

  const handleShare = () =>
    onOpenChildComponent(ShareConceptForm, { id, name });

  const handleOpenComments = () =>
    onOpenChildComponent(ConceptDetails, {
      activeTabId: TAB_COMMENTS,
      id,
    });

  return (
    <Actions
      actions={actions}
      limit={limit}
      onAddCreatives={handleAddCreatives}
      onEdit={handleEdit}
      onDelete={handleRemove}
      onArchive={handleArchive}
      onUnarchive={handleUnarchive}
      onMoveToPreview={handleMoveToPreview}
      onActivate={handleActivate}
      onInactivate={handleInactivate}
      onCopyLink={handleCopyLink}
      onExport={handleExport}
      onExportOriginal={handleExportOriginal}
      onExportToExcel={handleExportToExcel}
      onShare={handleShare}
      onOpenComments={handleOpenComments}
      color={color}
      withAddCreatives={withAddCreatives}
      withAddComment={withAddComment}
    />
  );
};

export default ConceptActions;
